import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import {
  Form,
  Formik,
  FormikConfig,
  FormikHelpers,
  FormikValues,
} from 'formik';
import useTranslate from '~/common/utils/translate';
import React, { PropsWithChildren } from 'react';
import { Button } from './Button';

type DefaultMessageModalDialogProps<Values> = {
  alignFooterLeft?: boolean;
  dialogHeaderText: string | React.ReactNode;
  submitText: string;
  isOpen: boolean;
  showCancelButton: boolean;
  onCancel: () => void;
  onSubmit: (values: Values, formikHelpers: FormikHelpers<Values>) => unknown;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | 'full';
  backgroundColor?: string;
  headerParams?: string[];
  footerParams?: string[];
  submitParams?: string[];
  submitButtonDangerVariant?: boolean;
  disableSubmitButton?: boolean;
  customSubmitButton?: React.ReactNode;
  hideSubmitButton?: boolean;
  contentWidth?: string;
} & FormikConfig<Values>;

export function DefaultModalDialog<Values extends FormikValues>({
  alignFooterLeft = false,
  dialogHeaderText,
  submitText,
  isOpen,
  onCancel,
  showCancelButton,
  size,
  backgroundColor,
  children,
  headerParams,
  footerParams,
  submitParams,
  submitButtonDangerVariant = false,
  disableSubmitButton = false,
  customSubmitButton: customButton,
  hideSubmitButton = false,
  ...props
}: PropsWithChildren<DefaultMessageModalDialogProps<Values>>) {
  const translate = useTranslate();
  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      closeOnOverlayClick={false}
      size={size}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent
        backgroundColor={
          backgroundColor === undefined ? 'white' : backgroundColor
        }
        overflowY="auto"
      >
        <Formik {...props}>
          {({ isSubmitting, isValid }) => (
            <Form>
              <ModalHeader pb={0}>
                {translate(dialogHeaderText, headerParams)}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>{children}</ModalBody>
              <ModalFooter
                justifyContent={alignFooterLeft ? 'flex-start' : 'flex-end'}
              >
                {customButton ? (
                  customButton
                ) : !hideSubmitButton ? (
                  <Button
                    isDisabled={!isValid}
                    isLoading={isSubmitting}
                    type="submit"
                    variant={submitButtonDangerVariant ? 'danger' : 'submit'}
                    disabled={disableSubmitButton}
                    mr={3}
                  >
                    {translate(submitText, submitParams)}
                  </Button>
                ) : null}
                {showCancelButton ? (
                  <Button variant="cancel" onClick={onCancel}>
                    {translate('cancelButton.text', footerParams)}
                  </Button>
                ) : null}
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  );
}
